import { useEffect, useState } from "react"
import { Box, Card, Checkbox, Chip, Collapse, CircularProgress, FormControlLabel, Grid, IconButton, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, useTheme } from "@mui/material"
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MapIcon from '@mui/icons-material/Map';
import PlaceIcon from '@mui/icons-material/Place';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import { Polygon, Marker } from '@react-google-maps/api';
import { ButtonBasic, TableBase } from "../../components"
import { showModal } from "react-redux-modal-provider";
import { useNavigate } from "react-router-dom";
import KmzService from "../../services/kmz.service";
import ModalCargarKmz from "../../modals/kmz/ModalCargarKmz";
import MapBase from "../../components/map/MapBase";

const getColorByCobertura = (tipo) => {
    switch (tipo?.toUpperCase()) {
        case 'DISPONIBLE': return { fill: '#4caf50', stroke: '#2e7d32' };
        case 'MEDIO':      return { fill: '#ffeb3b', stroke: '#f9a825' };
        case 'SATURADO':    return { fill: '#f44336', stroke: '#c62828' };
        default:            return { fill: '#9e9e9e', stroke: '#616161' };
    }
};

const TablaRadioEnlaceScreen = () => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const [items, setItems] = useState([]);
    const [showMap, setShowMap] = useState(false);
    const [filtros, setFiltros] = useState({ DISPONIBLE: true, MEDIO: true, SATURADO: true });
    const [clickedPoint, setClickedPoint] = useState(null);
    const [panelesEnPunto, setPanelesEnPunto] = useState([]);
    const [loadingPaneles, setLoadingPaneles] = useState(false);
    const [selectedPanel, setSelectedPanel] = useState(null);
    const [mapCenter, setMapCenter] = useState({ lat: -24.7821, lng: -65.4232 });
    const [mapZoom, setMapZoom] = useState(12);
    const [coordInput, setCoordInput] = useState('');
    const [coordError, setCoordError] = useState('');
    const navigate = useNavigate()
    const actualizar = async() =>{
        await KmzService.obtenerPoligonosRF().then((resp)=>{
            setItems(resp);
        }).catch(()=>{
            navigate('/')
        })
    }

    const columns = [
        {
            name: "id_pol",
            label: "ID",
            options: {
                filter: false,
                sort: false,
                display:false,
                viewColumns: false
            }
        },
        {
            name: "nombre_pol",
            label: "Nombre",
            options: {
                filter: true,
                sort: true,
            }
        },
        {
            name: "tipoTecnologia_pol",
            label: "Tecnologia",
            options: {
                filter: true,
                sort: true,
            }
        },
        {
            name: "tipoCobertura_pol",
            label: "Cobertura",
            options: {
                filter: true,
                sort: true,
            }
        },
        {
            name: 'isDeleted_pol',
            label: "Estado",
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value, tableMeta, updateValue) => {
                    if(!value){
                    return <Chip label="Activo" color="success" />
                    }
                    return <Chip label="Inactivo" color="error" />
                }
            }
        },
    ];
    const onGuardar = () => actualizar();

    const onOpenEditar = () => {
        showModal(ModalCargarKmz, { onGuardar: onGuardar});
    };

    const onClickMapa = async(coords) => {
        setClickedPoint(coords);
        setSelectedPanel(null);
        setLoadingPaneles(true);
        setPanelesEnPunto([]);
        try {
            const resp = await KmzService.panelesEnPuntoRF({ lat: coords.lat, lng: coords.lng });
            setPanelesEnPunto(resp || []);
        } catch (e) {
            setPanelesEnPunto([]);
        } finally {
            setLoadingPaneles(false);
        }
    };

    const getChipColor = (tipo) => {
        switch (tipo?.toUpperCase()) {
            case 'DISPONIBLE': return 'success';
            case 'MEDIO':      return 'warning';
            case 'SATURADO':   return 'error';
            default:           return 'default';
        }
    };

    const onBuscarCoordenada = () => {
        setCoordError('');
        const trimmed = coordInput.trim();
        if (!trimmed) { setCoordError('Ingrese coordenadas'); return; }
        const parts = trimmed.split(/[,;\s]+/).filter(Boolean);
        if (parts.length !== 2) { setCoordError('Formato: lat, lng'); return; }
        const lat = parseFloat(parts[0]);
        const lng = parseFloat(parts[1]);
        if (isNaN(lat) || isNaN(lng)) { setCoordError('Coordenadas inválidas'); return; }
        if (lat < -90 || lat > 90) { setCoordError('Latitud fuera de rango (-90 a 90)'); return; }
        if (lng < -180 || lng > 180) { setCoordError('Longitud fuera de rango (-180 a 180)'); return; }
        setMapCenter({ lat, lng });
        setMapZoom(15);
        onClickMapa({ lat, lng });
    };

    const onClickTableRow = (panel) => {
        setSelectedPanel(panel.id_pol);
        const item = items.find(it => it.id_pol === panel.id_pol);
        if (item?.arrCoordenadas_pol) {
            const coords = typeof item.arrCoordenadas_pol === 'string'
                ? JSON.parse(item.arrCoordenadas_pol)
                : item.arrCoordenadas_pol;
            if (coords.length > 0) {
                let sumLat = 0, sumLng = 0;
                coords.forEach(c => { sumLat += c[0]; sumLng += c[1]; });
                setMapCenter({ lat: sumLat / coords.length, lng: sumLng / coords.length });
                setMapZoom(14);
            }
        }
    };

    useEffect(() => {
        actualizar();
        document.title = `Paneles RF`;
    }, [])

    return (
        <Box sx={{width:'100%',height: '100%',overflow:'auto', background: isDarkMode ? '#1a2332' : '#f5f5f5'}} className='side'>
            <Grid container p={3} display='flex' justifyContent='center'>
                <Grid item xs={6}>
                    <Typography sx={{color: isDarkMode ? '#e0e0e0' : '#000', fontWeight: 'bold'}} variant="h2">Tabla Paneles</Typography>
                </Grid>
                <Grid item xs={6} sx={{display: 'flex', justifyContent: 'end'}}>
                    <ButtonBasic
                        textButton="Subir KMZ"
                        sx={{width: 200, height: 30}}
                        onClick={()=>onOpenEditar()}
                        variant='wk'
                        color="nextStep"
                    />
                </Grid>
                <Grid item xs={12} md={6} mt={2} sx={{marginBottom: 2}}>
                    <TableBase title='Resultados' columns={columns} data={items} />
                </Grid>
                <Grid item xs={12} mt={2} sx={{marginBottom: 15}}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            background: isDarkMode ? '#2d3a52' : '#fff',
                            border: `1px solid ${isDarkMode ? '#3d4a62' : '#e0e0e0'}`,
                            borderRadius: showMap ? '12px 12px 0 0' : '12px',
                            p: 1.5,
                            transition: 'all 0.3s ease',
                            '&:hover': { background: isDarkMode ? '#344158' : '#f5f5f5' }
                        }}
                        onClick={() => setShowMap(!showMap)}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <MapIcon sx={{ color: isDarkMode ? '#90caf9' : '#0364ff' }} />
                            <Typography sx={{ color: isDarkMode ? '#e0e0e0' : '#15294b', fontWeight: 'bold' }} variant="h6">
                                Mapa de Polígonos
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={filtros.DISPONIBLE}
                                            onChange={(e) => setFiltros(prev => ({ ...prev, DISPONIBLE: e.target.checked }))}
                                            size="small"
                                            sx={{ color: '#4caf50', '&.Mui-checked': { color: '#4caf50' }, p: 0.5 }}
                                        />
                                    }
                                    label={<Chip label="Disponible" size="small" sx={{ bgcolor: filtros.DISPONIBLE ? '#4caf50' : (isDarkMode ? '#3d4a62' : '#bdbdbd'), color: filtros.DISPONIBLE ? '#fff' : (isDarkMode ? '#9e9e9e' : '#757575'), fontWeight: 'bold', fontSize: 11, cursor: 'pointer' }} />}
                                    sx={{ m: 0 }}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={filtros.MEDIO}
                                            onChange={(e) => setFiltros(prev => ({ ...prev, MEDIO: e.target.checked }))}
                                            size="small"
                                            sx={{ color: '#ffeb3b', '&.Mui-checked': { color: '#ffeb3b' }, p: 0.5 }}
                                        />
                                    }
                                    label={<Chip label="Medio" size="small" sx={{ bgcolor: filtros.MEDIO ? '#ffeb3b' : (isDarkMode ? '#3d4a62' : '#bdbdbd'), color: filtros.MEDIO ? '#000' : (isDarkMode ? '#9e9e9e' : '#757575'), fontWeight: 'bold', fontSize: 11, cursor: 'pointer' }} />}
                                    sx={{ m: 0 }}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={filtros.SATURADO}
                                            onChange={(e) => setFiltros(prev => ({ ...prev, SATURADO: e.target.checked }))}
                                            size="small"
                                            sx={{ color: '#f44336', '&.Mui-checked': { color: '#f44336' }, p: 0.5 }}
                                        />
                                    }
                                    label={<Chip label="Saturado" size="small" sx={{ bgcolor: filtros.SATURADO ? '#f44336' : (isDarkMode ? '#3d4a62' : '#bdbdbd'), color: filtros.SATURADO ? '#fff' : (isDarkMode ? '#9e9e9e' : '#757575'), fontWeight: 'bold', fontSize: 11, cursor: 'pointer' }} />}
                                    sx={{ m: 0 }}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </Box>
                        </Box>
                        <IconButton size="small">
                            {showMap ? <ExpandLessIcon sx={{ color: isDarkMode ? '#90caf9' : '#15294b' }} /> : <ExpandMoreIcon sx={{ color: isDarkMode ? '#90caf9' : '#15294b' }} />}
                        </IconButton>
                    </Box>
                    <Collapse in={showMap}>
                        <Box
                            sx={{
                                border: `1px solid ${isDarkMode ? '#3d4a62' : '#e0e0e0'}`,
                                borderTop: 'none',
                                borderRadius: '0 0 12px 12px',
                                overflow: 'hidden'
                            }}
                        >
                            <Grid container>
                                <Grid item xs={12} lg={3} sx={{ borderRight: { lg: `1px solid ${isDarkMode ? '#3d4a62' : '#e0e0e0'}` }, background: isDarkMode ? '#2d3a52' : '#fff' }}>
                                    <Box sx={{ p: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                            <PlaceIcon sx={{ color: isDarkMode ? '#90caf9' : '#0364ff', fontSize: 20 }} />
                                            <Typography sx={{ color: isDarkMode ? '#e0e0e0' : '#15294b', fontWeight: 'bold', fontSize: 14 }}>
                                                Paneles en punto
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
                                            <TextField
                                                size="small"
                                                placeholder="-24.7821, -65.4232"
                                                value={coordInput}
                                                onChange={(e) => { setCoordInput(e.target.value); setCoordError(''); }}
                                                onKeyDown={(e) => { if (e.key === 'Enter') onBuscarCoordenada(); }}
                                                error={!!coordError}
                                                helperText={coordError}
                                                fullWidth
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        fontSize: 12,
                                                        height: 34,
                                                        background: isDarkMode ? '#1a2332' : '#f5f5f5',
                                                        color: isDarkMode ? '#e0e0e0' : '#15294b',
                                                        '& fieldset': { borderColor: isDarkMode ? '#3d4a62' : '#e0e0e0' },
                                                        '&:hover fieldset': { borderColor: isDarkMode ? '#90caf9' : '#0364ff' },
                                                        '&.Mui-focused fieldset': { borderColor: isDarkMode ? '#90caf9' : '#0364ff' },
                                                    },
                                                    '& .MuiFormHelperText-root': { fontSize: 10, mx: 0.5 },
                                                }}
                                            />
                                            <Tooltip title="Buscar coordenada">
                                                <IconButton
                                                    size="small"
                                                    onClick={onBuscarCoordenada}
                                                    sx={{
                                                        background: isDarkMode ? '#1a3a5c' : '#e3f2fd',
                                                        color: isDarkMode ? '#90caf9' : '#0364ff',
                                                        borderRadius: 1,
                                                        height: 34,
                                                        width: 36,
                                                        '&:hover': { background: isDarkMode ? '#254a6e' : '#bbdefb' },
                                                    }}
                                                >
                                                    <GpsFixedIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                        {!clickedPoint && (
                                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                                <PlaceIcon sx={{ color: isDarkMode ? '#3d4a62' : '#bdbdbd', fontSize: 48, mb: 1 }} />
                                                <Typography sx={{ color: isDarkMode ? '#6b788e' : '#9e9e9e', fontSize: 13 }}>
                                                    Haz click en el mapa para consultar los paneles en ese punto
                                                </Typography>
                                            </Box>
                                        )}
                                        {clickedPoint && loadingPaneles && (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                                <CircularProgress size={30} sx={{ color: isDarkMode ? '#90caf9' : '#0364ff' }} />
                                            </Box>
                                        )}
                                        {clickedPoint && !loadingPaneles && (
                                            <>
                                                <Typography sx={{ color: isDarkMode ? '#6b788e' : '#9e9e9e', fontSize: 11, mb: 1 }}>
                                                    {`${clickedPoint.lat.toFixed(6)}, ${clickedPoint.lng.toFixed(6)}`}
                                                </Typography>
                                                {panelesEnPunto.length === 0 ? (
                                                    <Box sx={{ textAlign: 'center', py: 3 }}>
                                                        <Typography sx={{ color: isDarkMode ? '#ff6b6b' : '#e53935', fontWeight: 'bold', fontSize: 13 }}>
                                                            Sin paneles en este punto
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <TableContainer sx={{ maxHeight: '50vh', overflow: 'auto' }} className="side">
                                                        <Table size="small" stickyHeader>
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell sx={{ background: isDarkMode ? '#1F2A40' : '#f5f5f5', color: isDarkMode ? '#90caf9' : '#15294b', fontWeight: 'bold', fontSize: 11, py: 0.5 }}>Panel</TableCell>
                                                                    <TableCell sx={{ background: isDarkMode ? '#1F2A40' : '#f5f5f5', color: isDarkMode ? '#90caf9' : '#15294b', fontWeight: 'bold', fontSize: 11, py: 0.5 }}>Tec.</TableCell>
                                                                    <TableCell sx={{ background: isDarkMode ? '#1F2A40' : '#f5f5f5', color: isDarkMode ? '#90caf9' : '#15294b', fontWeight: 'bold', fontSize: 11, py: 0.5 }}>Cobertura</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {panelesEnPunto.map((p) => {
                                                                    const isSelected = selectedPanel === p.id_pol;
                                                                    return (
                                                                    <TableRow
                                                                        key={p.id_pol}
                                                                        sx={{
                                                                            '&:hover': { background: isDarkMode ? '#344158' : '#f5f5f5' },
                                                                            cursor: 'pointer',
                                                                            background: isSelected ? (isDarkMode ? '#1a3a5c' : '#e3f2fd') : 'inherit',
                                                                            borderLeft: isSelected ? '3px solid #2196f3' : '3px solid transparent',
                                                                            transition: 'all 0.2s ease',
                                                                        }}
                                                                        onClick={() => onClickTableRow(p)}
                                                                    >
                                                                        <TableCell sx={{ color: isSelected ? (isDarkMode ? '#90caf9' : '#0d47a1') : (isDarkMode ? '#e0e0e0' : '#15294b'), fontWeight: isSelected ? 'bold' : 'normal', fontSize: 11, py: 0.5, borderColor: isDarkMode ? '#3d4a62' : '#e0e0e0' }}>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                {isSelected && <GpsFixedIcon sx={{ fontSize: 13, color: '#2196f3' }} />}
                                                                                {p.nombre_pol}
                                                                            </Box>
                                                                        </TableCell>
                                                                        <TableCell sx={{ color: isSelected ? (isDarkMode ? '#90caf9' : '#0d47a1') : (isDarkMode ? '#b0bec5' : '#6b788e'), fontWeight: isSelected ? 'bold' : 'normal', fontSize: 11, py: 0.5, borderColor: isDarkMode ? '#3d4a62' : '#e0e0e0' }}>{p.tipoTecnologia_pol}</TableCell>
                                                                        <TableCell sx={{ py: 0.5, borderColor: isDarkMode ? '#3d4a62' : '#e0e0e0' }}>
                                                                            <Chip label={p.tipoCobertura_pol} size="small" color={isSelected ? 'info' : getChipColor(p.tipoCobertura_pol)} sx={{ fontWeight: 'bold', fontSize: 10, height: 20 }} />
                                                                        </TableCell>
                                                                    </TableRow>
                                                                    );
                                                                })}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                )}
                                                <Typography sx={{ color: isDarkMode ? '#6b788e' : '#9e9e9e', fontSize: 11, mt: 1, textAlign: 'center' }}>
                                                    {`${panelesEnPunto.length} panel(es) encontrado(s)`}
                                                </Typography>
                                            </>
                                        )}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} lg={9}>
                                    <MapBase
                                        zoom={mapZoom}
                                        coordinates={mapCenter}
                                        mapContainerStyle={{ width: '100%', height: '60vh' }}
                                        marks={[]}
                                        onClick={onClickMapa}
                                        options={{ minZoom: 3, maxZoom: 20 }}
                                    >
                                        {clickedPoint && (
                                            <Marker position={clickedPoint} />
                                        )}
                                        {items?.filter(it => !it.isDeleted_pol && it.arrCoordenadas_pol && filtros[it.tipoCobertura_pol?.toUpperCase()])?.map((pol) => {
                                            const coords = typeof pol.arrCoordenadas_pol === 'string'
                                                ? JSON.parse(pol.arrCoordenadas_pol)
                                                : pol.arrCoordenadas_pol;
                                            const path = coords.map(c => ({ lat: c[0], lng: c[1] }));
                                            const colors = getColorByCobertura(pol.tipoCobertura_pol);
                                            const isSelected = selectedPanel === pol.id_pol;
                                            return (
                                                <Polygon
                                                    key={pol.id_pol}
                                                    paths={path}
                                                    options={{
                                                        fillColor: isSelected ? '#2196f3' : colors.fill,
                                                        fillOpacity: isSelected ? 0.45 : 0.2,
                                                        strokeColor: isSelected ? '#0d47a1' : colors.stroke,
                                                        strokeOpacity: isSelected ? 1 : 0.6,
                                                        strokeWeight: isSelected ? 4 : 2,
                                                        zIndex: isSelected ? 10 : 1,
                                                        clickable: true,
                                                    }}
                                                    onClick={(e) => onClickMapa({ lat: e.latLng.lat(), lng: e.latLng.lng() })}
                                                />
                                            );
                                        })}
                                    </MapBase>
                                </Grid>
                            </Grid>
                        </Box>
                    </Collapse>
                </Grid>
            </Grid>
        </Box>
    )
}

export default TablaRadioEnlaceScreen